/** React stuff */
import React from 'react';
/** utils */
import {
  calculateExpectedCost,
  calculateExpectedForWeek,
  getTask
} from '../../../../utils/lookahead-common';

import RenderName from '../RenderName';
import RenderActions from '../../columns/actions';
import RenderRoadblocks from '../RenderRoadblocks';
import RenderDuration from '../RenderDuration';
import RenderRoute from '../RenderRoute';
import RenderWeeklyCommit from '../RenderWeeklyCommit';
import RenderWeeklyMaterial from '../RenderWeeklyMaterial';
import RenderStatus from '../RenderStatus/RenderStatus';
import {
  recursiveTasksFilterWp,
  recursiveActivityFilter,
  getFlattenActivities
} from '../../../../utils';
import { findDeepGetTaskRecursive } from '../../GanttVisualization.helper';
import { calculateCommitment } from '../../../../views/weeklyPlan/weeklyPlan.helper';
import { TimerManagerSingleton } from '../../../../utils/timerManager';
import { renderCompanies } from '../../columns/companies';
import { renderPriority } from '../../columns/priority';
import { renderTags } from '../../columns/tags';
import { renderResponsible } from '../../columns/responsible';
import { renderLean } from '../../columns/leanStatus';
import { renderNumeric } from '../../columns/numeric';
import renderStart from '../../columns/startDate/startDate';
import { renderWeight } from '../../columns/weight';
import { renderDescription } from '../../columns/description';
import { renderProgress } from '../../columns/progress';
import { renderEnd } from '../../columns/endDate';

const buildReactRender = ({
  activities,
  gantt,
  t,
  onlyRead,
  lastLevelActivities,
  dropdownRemoveCallback,
  tableMetadata,
  volatileTaskData,
  toSelectSubs,
  setVisibleFormSubcontract,
  toSelectTags,
  setVisibleFormTags,
  toSelectResponsables,
  setEditedInput,
  projectState,
  updateAsyncActivity,
  updateGanttVisualizationState,
  data,
  showDrawer,
  updateRender,
  editedInput,
  resources,
  weeklyStatusObject,
  ganttAPI,
  modalModification,
  setModalModification,
  groupBy,
  tasksWeeklyPlan,
  setTasksWeeklyPlan,
  localActivitiesMap,
  updateTaskDrag,
  setLocalActivitiesMap,
  weeklyRangeObject
}) => {
  const timerManager = TimerManagerSingleton.getInstance();

  /** Global vars */
  const haveWeeklyPlan = weeklyStatusObject.isCommited;
  const dateRange = {
    start: weeklyStatusObject.start,
    end: weeklyStatusObject.end
  };

  /** Functions */

  /**
   * This function use common ref of weekly to re assign values
   * @param {*} task current task reference
   * @param {*} value new value to update
   */
  const updateCommitment = async (task) => {
    const tasks = tasksWeeklyPlan.value;
    const findTaskIndex = tasks.findIndex((e) => e.id === task.id);
    tasks[findTaskIndex] = task;
    tasksWeeklyPlan.value = tasks;
  };

  /**
   * This function updates following resource fields:
   * remaining_quantity
   * tagResource
   * quantity_parcial
   * commitment_percentaje
   * current_commitment_partial
   * @param {*} resourceMaterial_taks Array of already filtered resources
   * @param {*} taskRef Task lookahead memory ref
   * @param {*} ganttRef Gantt dhtmlx memory ref
   */

  const updateWeeklyMaterialValues = (
    resourceMaterial_taks,
    taskRef,
    ganttRef
  ) => {
    /** update remaining quantity  */
    const remainQuant = taskRef.total_quantity - taskRef.actual_quantity;
    if (taskRef.remaining_quantity !== remainQuant) {
      taskRef.remaining_quantity = remainQuant;
      ganttRef.remaining_quantity = taskRef.remaining_quantity;
    }

    let tagResource = '';
    if (resourceMaterial_taks) {
      const materialObject = resourceMaterial_taks.filter(
        (el) => el.id === taskRef.materialId
      );
      tagResource = materialObject[0] ? materialObject[0].material_label : null;
    }

    let quantity_parcial = 0;
    if (haveWeeklyPlan) {
      const find = haveWeeklyPlan.taskcommitments.find(
        (e) => e.taskId === taskRef.id
      );
      if (find && !isNaN(find.commitment_percentaje)) {
        quantity_parcial = find.current_commitment_partial;
        taskRef.quantity_parcial = quantity_parcial;
        ganttRef.quantity_parcial = taskRef.quantity_parcial;
      }
    } else if (taskRef.commitment_percentaje < taskRef.progress) {
      let newCompromise = taskRef.progress;
      if (taskRef.total_quantity) {
        newCompromise =
          taskRef.progress + (quantity_parcial * 100) / taskRef.total_quantity;
      }
      taskRef.commitment_percentaje = newCompromise;
      ganttRef.commitment_percentaje = taskRef.commitment_percentaje;
      quantity_parcial = 0;
    } else {
      if (!isNaN(taskRef.commitment_percentaje)) {
        if (gantt.isWeeklyCommited && !gantt.isWeeklyCommited.closed) {
          return false;
        }
        quantity_parcial =
          ((taskRef.commitment_percentaje - taskRef.progress) *
            taskRef.total_quantity) /
          100;
        if (taskRef.commitment_percentaje < taskRef.progress) {
          quantity_parcial = 0;
          taskRef.quantity_parcial = quantity_parcial;
          ganttRef.quantity_parcial = taskRef.quantity_parcial;
        }
      }
    }

    taskRef.current_commitment_partial =
      parseFloat(quantity_parcial).toFixed(2);
    ganttRef.current_commitment_partial = taskRef.current_commitment_partial;
    taskRef.quantity_parcial = parseFloat(quantity_parcial).toFixed(2);
    ganttRef.quantity_parcial = taskRef.quantity_parcial;

    ganttRef.tagResource = tagResource;
  };

  /**
   * This function updates following weekly commit fields:
   * -commitment_percentaje
   * -recalcCommitment
   * -showPureSpanCommitment
   * @param {*} taskRef Task lookahead memory ref
   * @param {*} ganttRef Gantt dhtmlx memory ref
   * @param {*} activityRef Activity ref from lookahead
   */
  const updateWeeklyCommitValues = (taskRef, ganttRef, activityRef) => {
    const parentTask = getParentLookaheadRef(taskRef);

    if (
      parseFloat(taskRef.commitment_percentaje) > 100 ||
      parseFloat(ganttRef.commitment_percentaje) > 100
    ) {
      taskRef.commitment_percentaje = 100;
      ganttRef.commitment_percentaje = 100;
    }

    /** Defines if already have a weekly, and this task is included in it */
    const localActivityRef = localActivitiesMap[activityRef.id];
    const findCommitedTask = haveWeeklyPlan?.taskcommitments?.find(
      (e) => e.taskId === taskRef.id
    );

    if (
      haveWeeklyPlan &&
      localActivityRef &&
      localActivityRef.recalcCommitment &&
      findCommitedTask &&
      !isNaN(findCommitedTask.commitment_percentaje)
    ) {
      activityRef.recalcCommitment = localActivityRef.recalcCommitment;
      calculateCommitment(
        taskRef,
        parentTask || activityRef,
        updateCommitment,
        haveWeeklyPlan,
        activityRef
      );
      const valueColumn = findCommitedTask.commitment_percentaje || 0;
      let percentVal = valueColumn.toFixed
        ? valueColumn.toFixed(2)
        : valueColumn;
      /** Bug fix: 843 */
      volatileTaskData[taskRef.id] = {
        ...volatileTaskData[taskRef.id],
        commitment_percentaje: findCommitedTask.commitment_percentaje
      };
      if (parseFloat(percentVal) > 100) {
        percentVal = 100;
      }
      taskRef.commitment_percentaje = findCommitedTask.commitment_percentaje;
      ganttRef.commitment_percentaje = findCommitedTask.commitment_percentaje;
      taskRef.showPureSpanCommitment = true;
      ganttRef.showPureSpanCommitment = true;
    } else if (!ganttRef.hasCustomCommitmentPercentaje) {
      const task = taskRef;
      const findActivity = activityRef;
      task.expected_cost = calculateExpectedCost(
        task,
        ganttAPI,
        findActivity.calendarId
      );
      ganttRef.expected_cost = task.expected_cost;
      try {
        const expWeek = calculateExpectedForWeek(
          task,
          ganttAPI,
          findActivity.calendarId,
          null,
          dateRange
        );
        task.commitment_percentaje = parseFloat(expWeek).toFixed(2);
        ganttRef.commitment_percentaje = task.commitment_percentaje;
        task.expectedweek = parseFloat(expWeek).toFixed(2);
        ganttRef.expectedweek = task.expectedweek;

        if (task.commitment_percentaje < task.progress) {
          let quantity_parcial = 0;
          let newCompromise = task.progress;
          if (task.total_quantity) {
            newCompromise =
              task.progress + (quantity_parcial * 100) / task.total_quantity;
          }
          task.commitment_percentaje = newCompromise;
          ganttRef.commitment_percentaje = task.commitment_percentaje;
          quantity_parcial = 0;
        }
        const quantity_parcial_percentaje = parseFloat(
          parseFloat(task.commitment_percentaje || 0) -
            parseFloat(task.progress)
        );
        task.quantity_parcial_percentaje = quantity_parcial_percentaje;
        ganttRef.quantity_parcial_percentaje = task.quantity_parcial_percentaje;
      } catch (e) {}
    }
  };

  /**
   * This function search paren task if there exist
   * @param {*} taskRef Child ref object to check his parent
   * @returns Parent lookahead ref for given task ref
   */
  const getParentLookaheadRef = (taskRef) => {
    let parentTask = null;
    if (taskRef) {
      if (taskRef.parent_id) {
        const doesExistAtReferenceParent = findDeepGetTaskRecursive(
          activities,
          'id',
          taskRef.parent_id
        );
        parentTask = doesExistAtReferenceParent || null;
      }
    }

    return parentTask;
  };

  /** This functions updates the current hashmap to handle memory ref,  */
  const updateHashMapMemoryRef = () => {
    if (!gantt) return;
    let resourceMaterial_taks;
    if (resources) {
      resourceMaterial_taks =
        resources &&
        resources.filter(function (rs) {
          return rs.type === 'material';
        });
    }

    const ganttTreeRef = gantt.getTaskByTime();
    ganttTreeRef &&
      ganttTreeRef.forEach((node) => {
        const { taskRef, activityRef } =
          getReferenceFromLookaheadWithActivityRef(node) || {
            taskRef: null,
            activityRef: null
          };
        /** This map includes lookahead view memory ref, acvtivity lookahead memory ref, and finally task at gantt dhtmlx memory ref */
        if (!taskRef) return;

        const commitCallback = () => {
          updateWeeklyCommitValues(taskRef, node, activityRef);
        };
        timerManager.registerAutoTimeout(
          commitCallback,
          500,
          'weeklyCommitValuesTimer'
        );

        const materialCallback = () => {
          updateWeeklyMaterialValues(resourceMaterial_taks, taskRef, node);
        };
        timerManager.registerAutoTimeout(
          materialCallback,
          1500,
          'weeklyMaterialValuesTimer'
        );
      });
  };

  /** updates values after each weekly plan update at react, or after autoschedule at gantt */
  const refreshView = () => {
    /** map activities and set hidden, according to filters */
    const lastLevelActivitiesFiltered = lastLevelActivities.activities;
    const filterWeek = ['Will', 'Committed'];

    /** sort by default */
    const data = recursiveTasksFilterWp(
      lastLevelActivitiesFiltered,
      filterWeek,
      dateRange
    );
    const dataFilterActivities = recursiveActivityFilter(data);
    const dataFilterActivitiesShow = dataFilterActivities.filter(
      (el) => el.showWeeklyPlan === true
    );
    const cloneActivities = dataFilterActivitiesShow.filter(
      (el) => el.tasks.length
    );
    const flattenActivities = getFlattenActivities(cloneActivities).filter(
      (el) => el.showWeeklyPlan
    );
    setTasksWeeklyPlan({
      value: flattenActivities
    });
    if (haveWeeklyPlan) {
      /** recalc activity on component activiy */
      cloneActivities.map((act) => {
        act.recalcCommitment = true;
        localActivitiesMap[act.id] = act;
        return act;
      });
    }

    const refreshCallback = () => {
      updateHashMapMemoryRef();
    };
    timerManager.registerAutoTimeout(refreshCallback, 1000, 'refreshViewTimer');
    return cloneActivities;
  };
  gantt && (gantt.refreshView = refreshView);

  /** get reference task (taskFromLookahead) from activities */
  const getReferenceFromLookaheadWithActivityRef = (ganttRefTask) => {
    const task = ganttRefTask;
    const activityReferenceLvl = activities.find(
      (ac) => ac.id === task?.activityReference?.proplannerId
    );
    if (!activityReferenceLvl) return;
    const newLookaheadTaskRef = getTask(task.id, null, activityReferenceLvl);
    if (newLookaheadTaskRef?.length) {
      return {
        taskRef: newLookaheadTaskRef[0],
        activityRef: activityReferenceLvl
      };
    }
    return { taskRef: null, activityRef: activityReferenceLvl };
  };

  /** Onrender assign functions */

  /**
   * This function handles common dropdown behaviour at onrender React custom components
   * @param {*} column Current column to define onrender method
   * @param {*} newCommonObject Common props to use any render component
   */
  const defineRenderDropdown = (column, newCommonObject) => {
    if (column.name === 'subcontractId') {
      column.template = (task) =>
        renderCompanies({
          ...newCommonObject,
          lastLevelActivities,
          task,
          subs: toSelectSubs,
          callbackOnClickRemove: dropdownRemoveCallback(task, column),
          callbackOnClickCreate: () => {
            setVisibleFormSubcontract(true);
            gantt.toHardAssignSubcontract = task.id;
          }
        });
    }

    if (column.name === 'tags') {
      column.template = (task) =>
        renderTags({
          ...newCommonObject,
          lastLevelActivities,
          task,
          tags: toSelectTags,
          callbackOnClickRemove: dropdownRemoveCallback(task, column),
          callbackOnClickCreate: () => {
            setVisibleFormTags(true);
            gantt.toHardAssignTag = task.id;
          }
        });
    }
    if (column.name === 'responsables') {
      column.template = (task) =>
        renderResponsible({
          ...newCommonObject,
          lastLevelActivities,
          task,
          responsables: toSelectResponsables
        });
    }
  };

  const defineRenderName = (column, newCommonObject, index) => {
    if (column.name === 'name') {
      const renderNameProps = {
        ...newCommonObject,
        projectState
      };
      column.template = (task, node) => {
        return RenderName.layout({
          ...renderNameProps,
          task,
          gantt
        });
      };
    }
  };

  const defineRenderActions = (column, newCommonObject, index) => {
    if (column.name === 'actions') {
      column.template = (task) => {
        return RenderActions.template({
          task,
          gantt,
          onlyRead
        });
      };
    }
  };

  const defineRenderRoadblocks = (column, newCommonObject, index) => {
    if (column.name === 'roadblocks') {
      const renderRoadblocksProps = {
        ...newCommonObject,
        setEditedInput,
        index,
        tableConfig: tableMetadata,
        projectState,
        updateAsyncActivity,
        updateGanttVisualizationState,
        data,
        showDrawer,
        updateRender
      };
      column.template = (task, node) =>
        RenderRoadblocks({ ...renderRoadblocksProps, task, node, gantt });
    }
  };
  const defineRenderPriority = (column, newCommonObject) => {
    if (column.name === 'priority') {
      column.template = (task) =>
        renderPriority({
          ...newCommonObject,
          task
        });
    }
  };

  const defineRenderLeanStatus = (column, newCommonObject) => {
    if (column.name === 'lean_status') {
      column.template = (task) =>
        renderLean({
          ...newCommonObject,
          task
        });
    }
  };

  const defineRenderNumeric = (column) => {
    const genericNumericColumns = [
      'real_endowment',
      'plan_endowment',
      'cost',
      'expected_cost'
    ];

    if (genericNumericColumns.includes(column.name)) {
      column.template = (task) =>
        renderNumeric({
          task,
          column,
          t,
          onlyRead,
          projectState
        });
    }
  };

  const defineWeeklyFields = (column, newCommonObject) => {
    const commonWeeklyFieldProps = {
      ...newCommonObject,
      volatileTaskData,
      setEditedInput,
      editedInput,
      resources,
      haveWeeklyPlan,
      lastLevelActivities,
      dateRange,
      getReferenceFromLookahead: getReferenceFromLookaheadWithActivityRef,
      refreshView,
      tasksWeeklyPlan,
      localActivitiesMap,
      updateCommitment
    };

    if (column.name === 'commitment_percentaje') {
      column.template = (task) => {
        const renderWeeklyCommitProps = {
          ...commonWeeklyFieldProps,
          task
        };
        return RenderWeeklyCommit(renderWeeklyCommitProps);
      };
    }

    if (column.name === 'quantity_parcial') {
      column.template = (task) => {
        const renderWeeklyMaterialProps = {
          ...commonWeeklyFieldProps,
          task
        };
        return task ? RenderWeeklyMaterial(renderWeeklyMaterialProps) : '';
      };
    }
  };

  /** Main return */
  /** this function sets the onrender property for each column */
  const renderActivityColums = () => {
    tableMetadata.map((column, index) => {
      /** common params */
      const newCommonObject = {
        column,
        activities,
        gantt,
        t,
        onlyRead,
        index
      };

      defineRenderDropdown(column, newCommonObject);
      defineRenderName(column, newCommonObject, index);
      defineRenderActions(column, newCommonObject, index);
      defineRenderRoadblocks(column, newCommonObject, index);
      defineRenderPriority(column, newCommonObject);
      defineRenderLeanStatus(column, newCommonObject);

      if (column.name === 'description') {
        column.template = (task) =>
          renderDescription({ task, column, t, onlyRead });
      }
      if (column.name === 'start_date') {
        column.template = (task) => {
          const renderStartProps = {
            task,
            onlyRead
          };
          return renderStart(renderStartProps);
        };
      }
      if (column.name === 'end_date') {
        column.template = (task) => {
          const renderEndProps = {
            task,
            onlyRead,
            column,
            modalModification,
            setModalModification,
            t
          };
          return renderEnd(renderEndProps);
        };
      }

      if (column.name === 'status') {
        column.template = (task) => {
          const renderStatusProps = {
            task,
            gantt
          };
          return RenderStatus(renderStatusProps);
        };
      }

      if (column.name === 'progress') {
        column.template = (task) => renderProgress(task, t, onlyRead);
      }

      if (column.name === 'duration') {
        const renderDurationProps = {
          ...newCommonObject,
          setEditedInput,
          index,
          ganttAPI,
          projectState
        };
        column.template = (task, node) =>
          RenderDuration({ ...renderDurationProps, task, node });
      }
      if (column.name === 'taskRoute') {
        column.template = (task) => RenderRoute({ task, column });
      }

      /** Fields only available on this groupping by */
      if (
        groupBy.criteria === 'activity' ||
        groupBy.criteria === 'activityId'
      ) {
        defineRenderNumeric(column, newCommonObject, index);
        defineWeeklyFields(column, newCommonObject, index);

        if (['ponderator'].includes(column.name)) {
          column.template = (task) => renderWeight({ task, column, t });
        }
      }
      return column;
    });
  };

  renderActivityColums();
};

export default buildReactRender;
